import {OrderScope} from '../types'

/**
 * Minimal order data transfer object required for OrderChange process.
 * Based on ordersApi payload but contains only fields necessary to proceed with order modifications.
 * Extends OrderScope to ensure compatibility with OrderProcessContext.
 */
export interface OrderDto extends OrderScope {
  /**
   * Checksum used to verify order state consistency with backend system
   */
  checkSum?: string
  /**
   * Indicates whether a PIN is required to confirm the order
   */
  confirmationPinRequired?: boolean
  /**
   * Site contact name and number
   */
  contactInformation?: SiteContact
  /**
   * Reference to contract or quotation number
   */
  contractNumber?: string
  /**
   * Contract item position number
   */
  contractItem?: string
  /**
   * Order line items (always exactly one line item per order)
   */
  lineItems: [OrderLineItem]
  /**
   * Unique Identifier for the order (narrows OrderScope's optional orderId to required)
   */
  orderId: string
  /**
   * Reference to business key of an order in the backend system
   */
  orderNumber: string
  /**
   * Status information of the order
   */
  orderStatus: OrderStatus
  /**
   * The requested schedule of the delivery (one schedule per order)
   */
  schedules: [DeliverySchedule]
  /**
   * Deliveries assign to the order
   */
  deliveries?: DeliveryDto[]
  /**
   * Current progress of the delivery
   */
  deliveryProgress: DeliveryProgressType
  /**
   * The date the order is expected to be executed
   */
  shippingDate: string
  /**
   * Indicates if an order is delivered to the customer or picked-up by the customer
   */
  shippingType: ShippingType
  /**
   * Is test is booked for this order
   */
  isTestBooked: boolean
  /**
   * Phone number for dispatch communication
   */
  dispatchPhoneNumber?: string
  /**
   * Customer reference, PO number
   */
  customerReference?: string | null
  /**
   * Order management capabilities and restrictions
   */
  orderManagement?: OrderManagement
  /**
   * Note containing header and item texts (driver instructions stored in itemTexts[1-3])
   */
  note?: OrderNote
 /**
   * Variation code indicating delivery schedule adjustments (RMC only)
   */
  variationCode?: string
  /**
   * ISO 8601 duration offset associated with the variation code (e.g. 'PT30M')
   */
  variationCodeOffset?: string
  /**
   * Adjusted date/time associated with the variation code
   */
  variationCodeAdjustedTime?: string
}

/**
 * Note structure containing header and item level texts
 */
export interface OrderNote {
  /**
   * Header level text entries
   */
  headerTexts: string[]
  /**
   * Item level text entries (driver instructions are typically in indices 1-3)
   */
  itemTexts: string[]
}

/**
 * Order line item details
 */
export interface OrderLineItem {
  /**
   * Reference to the position number of the row in the order
   */
  itemNumber: string
  /**
   * Reference to business key of the material in the backend system
   */
  materialNumber: string
  /**
   * Description of the material of the order
   */
  materialDescription: string
  /**
   * Alternate material description, typically used for customer-facing display
   */
  customerMaterialDescription?: string
  /**
   * Additional material attributes in some regions (e.g., Slump, SetControl)
   */
  materialAttributes?: MaterialAttribute[]
  /**
   * quantity ordered
   */
  quantity: number
  /**
   * Min quantity ordered
   */
  minQuantity?: number
  /**
   * Max quantity ordered
   */
  maxQuantity?: number
  /**
   * The unit of measurement of the quantity order
   */
  quantityUom: string
  /**
   * Indicates expectation for PlusLoad feature (additional quantity expected)
   */
  quantityChangeExpected?: boolean
}

/**
 * Minimal delivery data transfer object required for OrderChange process.
 * Based on ordersApi payload but contains only fields necessary to proceed with order modifications.
 */
export interface DeliveryDto {
  /**
   * Unique Identifier for the delivery/ticket/shipment s
   */
  deliveryId: string
  /**
   * business identifier of the delivery that is normally also printed on delivery note
   */
  deliveryNumber?: null | string
  /**
   * Status information of the delivery
   */
  deliveryStatus: DeliveryStatusType
}

/**
 * Status information of the order
 */
export enum OrderStatus {
  Cancelled = 'cancelled',
  Confirmed = 'confirmed',
  Delivered = 'delivered',
  InProgress = 'inProgress',
  PartiallyDelivered = 'partiallyDelivered',
  PartiallyShipped = 'partiallyShipped',
  Pending = 'pending',
  Reserved = 'reserved',
  Shipped = 'shipped',
  Rejected = 'rejected',
  Closed = 'closed',
  PaymentPending = 'paymentPending',
  Backorder = 'backorder'
}

export enum DeliveryStatusType {
  cancelled = 'cancelled',
  completed = 'completed',
  inTransit = 'inTransit',
  loaded = 'loaded',
  loading = 'loading',
  open = 'open',
  unknown = 'unknown',
  unloaded = 'unloaded',
  unloading = 'unloading',
  waitingForLoad = 'waitingForLoad',
  waitingForUnload = 'waitingForUnload',
  invoiced = 'invoiced',
  returned = 'returned'
}

/**
 * Indicates if an order is delivered to the customer or picked-up by the customer
 */
export enum ShippingType {
  Collect = 'collect',
  Deliver = 'deliver'
}

/**
 * Additional attribute information for material line items
 */
export interface MaterialAttribute {
  /**
   * Type of the material attribute, (e.g., Slump, SetControl)
   */
  key: string
  /**
   * Value of the material attribute
   */
  value: string
  /**
   * Customer-friendly description of the material attribute value
   */
  description?: string
}

/**
 * Site contact information for delivery location
 */
export interface SiteContact {
  /**
   * Name of the contact person who placed the order
   */
  orderPlacerName: string
  /**
   * Phone number of the contact person who placed the order
   */
  orderPlacerPhoneNumber: string
  /**
   * Name of the contact person at the delivery site
   */
  siteContactName: string
  /**
   * Phone number of the contact person at the delivery site
   */
  siteContactNumber: string
}

/**
 * Order management capabilities defining what actions can be performed on an order
 */
export interface OrderManagement {
  /**
   * Whether order management features are enabled for this order
   */
  isEnabled: boolean
  /**
   * Whether the order can be modified
   */
  isChangeable: boolean
  /**
   * Whether the order can be cancelled
   */
  isCancellable: boolean
  /**
   * UTC timestamp until which the order can be cancelled
   */
  cancellableUntil: string
}

/**
 * Delivery schedule information for order execution timing and logistics
 */
export interface DeliverySchedule {
  /**
   * Scheduled start date for delivery
   */
  scheduleStartDate: string
  /**
   * Earliest possible start date for delivery
   */
  scheduleEarliestStartDate: string
  /**
   * Time spacing between truck arrivals (ISO 8601 duration format)
   */
  arrivalSpacing: string
  /**
   * Flow rate in quantity per hour
   */
  quantityPerHour: number
  /**
   * Duration to spend on job site (ISO 8601 duration format)
   */
  timeOnJobSite: string
  /**
   * Maximum load size per truck
   */
  maxLoadSize: number
  /**
   * Maximum load space size per truck
   */
  maxLoadSpaceSize: number
  /**
   * Plus load expected indicator
   */
  quantityChangeExpected: boolean
}
/**
 * Current progress of the delivery
 */
export interface DeliveryProgressType {
  assignedDeliveries: number
  assignedQuantity: number
  arrivedDeliveries: number
  arrivedQuantity: number
  deliveredDeliveries: number
  deliveredQuantity: number
  shippedDeliveries: number
  shippedQuantity: number
}

/**
 * Order Change Request Status API types
 *
 * These types represent the GET /OrderManagement/v2/Change/{orderId}/requests endpoint response.
 */
export interface OrderChangeRequestFieldChange<T = string> {
  oldValue?: T
  newValue?: T
  isChanged: boolean
  requiresManualApproval?: boolean
}

export interface OrderChangeRequestDateChange {
  time?: {isChanged: boolean}
  date?: {isChanged: boolean}
  oldValue?: string
  newValue?: string
  isChanged: boolean
}

export interface OrderChangeRequestContactInfo {
  orderPlacerName?: string
  orderPlacerPhoneNumber?: string
  siteContactName?: OrderChangeRequestFieldChange
  siteContactNumber?: OrderChangeRequestFieldChange
}

export interface OrderChangeRequestMaterial {
  materialNumber: string
  materialDescription: string
  customerMaterialDescription?: string
}

export interface OrderChangeRequestSlump {
  value: string
  description: string
}

export interface OrderChangeRequestData {
  requiresManualApproval?: boolean
  isConfirmedByCustomer?: OrderChangeRequestFieldChange<boolean>
  shippingDate?: OrderChangeRequestDateChange
  quantity?: OrderChangeRequestFieldChange<number>
  quantityChangeExpected?: OrderChangeRequestFieldChange<boolean>
  contactInformation?: OrderChangeRequestContactInfo
  flowRate?: OrderChangeRequestFieldChange<number>
  loadSpace?: OrderChangeRequestFieldChange
  timeOnJobSite?: OrderChangeRequestFieldChange
  customerReference?: OrderChangeRequestFieldChange
  isTestBooked?: OrderChangeRequestFieldChange<boolean>
  material?: OrderChangeRequestFieldChange<OrderChangeRequestMaterial>
  contractNumber?: OrderChangeRequestFieldChange
  contractItem?: OrderChangeRequestFieldChange
  slump?: OrderChangeRequestFieldChange<OrderChangeRequestSlump>
  setControl?: OrderChangeRequestFieldChange
  checkSum?: string
}

export interface OrderChangeRequestStatus {
  requestId: string
  createdOn: string
  createdBy: string
  status: string
  rejectionReason?: string
  modifiedOn?: string
  countryId: string
  orgUnitId: string
  businessLine: string
  customerId: string
  siteId: string
  orderId: string
  orderNumber: string
  orderItem: string
  requestData: OrderChangeRequestData
}
